import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Gauge, Upload, Loader2, FileText, Sparkles, ArrowRight, RotateCcw, Wand2,
} from "lucide-react";

const scoreColor = (s) => (s >= 85 ? "#16a34a" : s >= 70 ? "#2563EB" : s >= 50 ? "#F59E0B" : "#dc2626");
const scoreLabel = (s) => (s >= 85 ? "Excellent" : s >= 70 ? "Good" : s >= 50 ? "Average" : "Needs work");

const ScoreRing = ({ score }) => {
  const r = 84;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  const color = scoreColor(score);
  return (
    <div className="relative h-52 w-52" data-testid="ats-score-ring">
      <svg className="h-52 w-52 -rotate-90" viewBox="0 0 200 200">
        <circle cx="100" cy="100" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="14" />
        <motion.circle
          cx="100" cy="100" r={r} fill="none" stroke={color} strokeWidth="14" strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.1, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className="font-heading font-bold text-6xl tracking-tight"
          style={{ color }}
          initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}
          data-testid="ats-score-value"
        >
          {score}
        </motion.span>
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-1">/ 100</span>
      </div>
    </div>
  );
};

const AtsScore = () => {
  const [resumeText, setResumeText] = useState("");
  const [jobDesc, setJobDesc] = useState("");
  const [uploading, setUploading] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/improve/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResumeText(res.data.text || "");
      if (!res.data.text_len) toast.warning("Couldn't read much text — paste it below instead.");
      else toast.success(`${file.name} read — ${res.data.text_len} characters`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const getScore = async () => {
    if (resumeText.trim().length < 30) { toast.error("Please upload or paste your resume first."); return; }
    setScoring(true);
    setResult(null);
    try {
      const res = await api.post("/ats/score", { resume_text: resumeText.trim(), job_description: jobDesc.trim() || null });
      setResult(res.data);
      setTimeout(() => document.getElementById("ats-result")?.scrollIntoView({ behavior: "smooth" }), 150);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not score your resume");
    } finally { setScoring(false); }
  };

  const reset = () => { setResult(null); setResumeText(""); setJobDesc(""); };

  return (
    <div className="font-body min-h-[calc(100vh-4rem)]" data-testid="ats-page">
      {/* Hero */}
      <section className="max-w-3xl mx-auto px-5 pt-14 pb-6 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium mb-5">
          <Gauge className="h-3.5 w-3.5 text-accent" /> Free ATS Score — no sign-in needed
        </div>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight">
          How well does your resume score?
        </h1>
        <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
          Get an instant ATS (Applicant Tracking System) score out of 100. Paste a job description for an accurate
          match, or check your resume's general ATS-readiness.
        </p>
      </section>

      {/* Input */}
      <section className="max-w-3xl mx-auto px-5 pb-16">
        <Card className="p-6 sm:p-8 space-y-6" data-testid="ats-input-card">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="font-heading font-semibold text-base">Your resume</Label>
              <label className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-primary text-primary-foreground cursor-pointer hover:opacity-90 transition-opacity font-semibold" data-testid="ats-upload-label">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Reading…" : "Upload PDF/DOC"}
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={upload} data-testid="ats-upload-input" />
              </label>
            </div>
            <Textarea
              rows={8}
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              placeholder="Paste your resume text here — or upload a PDF/DOC above and we'll read it for you…"
              data-testid="ats-resume-input"
            />
          </div>

          <div>
            <Label className="font-heading font-semibold text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" /> Job description
              <span className="text-xs font-normal text-muted-foreground">(optional — for an accurate match)</span>
            </Label>
            <Textarea
              rows={5}
              value={jobDesc}
              onChange={(e) => setJobDesc(e.target.value)}
              placeholder="Paste the job description you're targeting to see how well you match it…"
              className="mt-2"
              data-testid="ats-jd-input"
            />
          </div>

          <Button
            onClick={getScore}
            disabled={scoring}
            size="lg"
            className="w-full rounded-full font-semibold text-base h-12"
            data-testid="ats-score-btn"
          >
            {scoring ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing your resume…</> : <><Gauge className="h-4 w-4 mr-2" /> Get my ATS score</>}
          </Button>
        </Card>

        {/* Result */}
        {result && (
          <motion.div
            id="ats-result"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="mt-8"
            data-testid="ats-result"
          >
            <Card className="p-8 text-center overflow-hidden relative">
              <div className="flex justify-center mb-4"><ScoreRing score={result.score} /></div>
              <div
                className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold mb-3"
                style={{ backgroundColor: `${scoreColor(result.score)}1a`, color: scoreColor(result.score) }}
                data-testid="ats-verdict"
              >
                {scoreLabel(result.score)} · {result.verdict}
              </div>
              <p className="text-muted-foreground max-w-xl mx-auto" data-testid="ats-summary">{result.summary}</p>
              <p className="text-xs text-muted-foreground/70 mt-3">
                {result.matched_job ? "Scored against the job description you provided." : "General ATS-readiness score. Paste a job description above for a targeted match."}
              </p>

              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <Link to="/improve">
                  <Button size="lg" className="rounded-full font-semibold bg-accent text-accent-foreground hover:bg-accent/90" data-testid="ats-improve-cta">
                    <Wand2 className="h-4 w-4 mr-1.5" /> Improve my resume <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                </Link>
                <Button variant="outline" size="lg" className="rounded-full font-semibold" onClick={reset} data-testid="ats-reset-btn">
                  <RotateCcw className="h-4 w-4 mr-1.5" /> Check another
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        {!result && (
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Sparkles className="h-4 w-4 text-accent" /> Powered by AI · Instant · 100% free
          </div>
        )}
      </section>
    </div>
  );
};

export default AtsScore;
